import { ROUTE_PATHS } from '@/constants';
import { clearClientSession } from '@/lib/clear-client-session';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Any of these can be the reason the gate never opened, and a reload preserves sessionStorage,
// so a retry that left them in place would re-enter the same wedge.
const RECOVERY_BLOCKING_KEYS = ['__nav_lock_until', '__last_navigation', '__last_logout_ts'];

// Terminal state for the initial auth gate's watchdog (POR-433). Both actions bypass the router
// deliberately: the in-app auth query is what's wedged, so only a full document load resets it.
const AuthCheckFailed: FC = () => {
  const { t } = useTranslation('login');

  const handleRetry = useCallback(() => {
    try {
      for (const key of RECOVERY_BLOCKING_KEYS) sessionStorage.removeItem(key);
    } catch (error) {
      // Failing here means the reload re-enters the same wedge, so it must not be silent.
      console.error('[auth-gate] could not clear recovery keys before retrying', error);
    }
    window.location.reload();
  }, []);

  // Deliberately no `?redirect=`, unlike lib/router.tsx's handleUnauthorized: the login page
  // stores that param and usePostLoginRedirect then navigates to it with no session check, which
  // sent this button back to the wedged route. See docs/SESSION_EXPIRATION_FIX.md's watchdog item.
  const handleReturnToLogin = useCallback(() => {
    clearClientSession();
    window.location.assign(ROUTE_PATHS.LOGIN);
  }, []);

  return (
    <Stack
      alignItems="center"
      data-testid="auth-check-failed"
      fullHeight
      fullWidth
      gap={4}
      justifyContent="center"
      orientation="vertical"
      role="alert"
    >
      <Text as="h1">{t('session_check_failed.title')}</Text>

      <Text as="p">{t('session_check_failed.message')}</Text>

      <Stack gap={3} orientation="horizontal">
        <Button data-testid="auth-check-retry-btn" onPress={handleRetry} visual="primary">
          {t('session_check_failed.retry')}
        </Button>

        <Button data-testid="auth-check-login-btn" onPress={handleReturnToLogin} visual="default">
          {t('session_check_failed.return_to_login')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default AuthCheckFailed;
